/**
 * The editor folder inside a `.glz` (CONTENT_MODEL_SPEC §4, §2.9).
 *
 * A bundle is the source view of a folder, and a scene or asset folder may
 * hold a nested `editor/` folder. Packed, that folder is the same thing one
 * level down: `editor/scene.gltf` plus `editor/assets/…`, beside the scene's
 * own `scene.gltf` and `assets/`. Nothing in the scene document names it, so
 * a reader that knows nothing of `editor/` opens the scene unchanged.
 *
 * Both halves are ordinary bundles, written and read by the ordinary bundle
 * code: `exportToGlzBlob({ exclude: [editorRoot] })` for the scene,
 * `exportSubtreeToGlzBlob(editorRoot)` for the editor document. This module
 * only nests one inside the other and takes it back out, so the editor
 * document's files keep their own namespace — `.glz` entry names are human,
 * and `assets/photo.png` may be different bytes on each side.
 */

import JSZip from 'jszip';
import { createZipBlob, deflateLevelForPath, type ZipEntry } from './zip/zipWriter';
import { toZipBytes } from '../loaders/formats/bundle/zipSniff';
import { GLTF_SCENE_FILE_NAME } from '../scenedata/constants';
import type { SceneFolder } from './SceneExporter';
import { isAbsoluteHttpUrl, isAssetsEntry } from './manifestValidator';

/** The nested folder's prefix inside a bundle (spec §2.1). */
export const EDITOR_BUNDLE_FOLDER = 'editor/';

async function entriesOf(archive: Blob): Promise<JSZip.JSZipObject[]> {
    const zip = await JSZip.loadAsync(toZipBytes(await archive.arrayBuffer()));
    return Object.values(zip.files).filter(file => !file.dir);
}

async function toEntry(file: JSZip.JSZipObject, path: string): Promise<ZipEntry> {
    return { path, data: await file.async('uint8array'), level: deflateLevelForPath(path) };
}

/**
 * Nest the editor document's bundle inside the scene's, under `editor/`.
 *
 * @param scene - the scene bundle, written with the editor subtree excluded.
 * @param editor - the editor subtree's own bundle.
 * @throws when `scene` already carries `editor/` entries, or `editor` has no
 *   document — either would make the packed folder mean something else.
 */
export async function packEditorBundle(scene: Blob, editor: Blob): Promise<Blob> {
    const sceneFiles = await entriesOf(scene);
    if (sceneFiles.some(file => file.name.startsWith(EDITOR_BUNDLE_FOLDER))) {
        throw new Error(`packEditorBundle: the scene bundle already has ${EDITOR_BUNDLE_FOLDER} entries`);
    }
    const editorFiles = await entriesOf(editor);
    if (!editorFiles.some(file => file.name === GLTF_SCENE_FILE_NAME)) {
        throw new Error(`packEditorBundle: the editor bundle has no ${GLTF_SCENE_FILE_NAME}`);
    }

    const entries = [
        ...await Promise.all(sceneFiles.map(file => toEntry(file, file.name))),
        ...await Promise.all(editorFiles.map(file => toEntry(file, EDITOR_BUNDLE_FOLDER + file.name))),
    ];
    return createZipBlob(entries);
}

/**
 * The editor document's bundle, taken back out of a packed `.glz`, or `null`
 * when the archive carries no `editor/scene.gltf`.
 *
 * The scene half needs no unpacking: the bundle reader reads `scene.gltf` and
 * `assets/` and nothing else, so the archive opens as the scene as it is.
 */
export async function readEditorBundle(archive: Blob): Promise<Blob | null> {
    const editorFiles = (await entriesOf(archive)).filter(file => file.name.startsWith(EDITOR_BUNDLE_FOLDER));
    if (!editorFiles.some(file => file.name === EDITOR_BUNDLE_FOLDER + GLTF_SCENE_FILE_NAME)) return null;
    const entries = await Promise.all(
        editorFiles.map(file => toEntry(file, file.name.slice(EDITOR_BUNDLE_FOLDER.length))),
    );
    return createZipBlob(entries);
}

/**
 * A `.glz` as the folder it packs (spec §4): `scene.gltf` as the document and
 * each relative `assets/` file it names as bytes, keyed by the document's own
 * uri. This is what a folder export hands a client to upload, so an editor
 * that imports a bundle writes it the way it writes any save: upload
 * `files`, repoint with `repointFolderDocument`, post `document`. The bundle
 * is never sent to the server whole, whose import repoints `files[]` only.
 *
 * Answers `null` for an archive with no `scene.gltf` (a legacy `scene.glxf`
 * bundle). The `editor/` folder is not part of the scene folder; it is
 * {@link readEditorBundle}'s.
 *
 * @throws when the document is not a glTF object (whose `files`, if present,
 *   is an array), names a relative file that is not one entry under
 *   `assets/`, or names one the archive lacks: a
 *   folder written from it would load without that file. A document with no
 *   `files` is a folder with no files.
 */
export async function readBundleFolder(archive: Blob): Promise<SceneFolder | null> {
    const zip = await JSZip.loadAsync(toZipBytes(await archive.arrayBuffer()));
    const documentEntry = zip.file(GLTF_SCENE_FILE_NAME);
    if (!documentEntry) return null;

    const text = await documentEntry.async('string');
    let manifest: unknown;
    try {
        manifest = JSON.parse(text);
    } catch {
        throw new Error(`readBundleFolder: ${GLTF_SCENE_FILE_NAME} is not JSON`);
    }
    const entries = (manifest as { files?: unknown } | null)?.files ?? [];
    if (!manifest || typeof manifest !== 'object' || !Array.isArray(entries)) {
        throw new Error(`readBundleFolder: ${GLTF_SCENE_FILE_NAME} is not a glTF document (an object whose files, if any, are an array)`);
    }

    const files = new Map<string, Blob>();
    for (const entry of entries as Array<{ uri?: unknown; mimeType?: unknown } | null>) {
        const uri = typeof entry?.uri === 'string' ? entry.uri : undefined;
        // Absolute entries (a library reference, foreign media) are nobody's folder file.
        if (!uri || isAbsoluteHttpUrl(uri) || files.has(uri)) continue;
        const path = uri.startsWith('./') ? uri.slice(2) : uri;
        if (!isAssetsEntry(path)) {
            throw new Error(`readBundleFolder: the document names ${uri}, which is not a folder file (assets/…)`);
        }
        const file = zip.file(path);
        if (!file) throw new Error(`readBundleFolder: the document names ${uri}, which the archive does not hold`);
        const type = typeof entry?.mimeType === 'string' ? entry.mimeType : '';
        files.set(uri, new Blob([await file.async('arraybuffer')], { type }));
    }
    return { document: new Blob([text], { type: 'model/gltf+json' }), files, existing: new Set() };
}
